/**
 * Betting scene — 8 horse cards in a row with number badges, names, silk
 * colors, and a live bet count chip. Tracks a countdown to `endsAtMs`.
 *
 * Polish pass: rounded silk-colored cards with drop shadows, big circular
 * number badges, animated bet-count chips that pulse when they tick up,
 * theme-vars throughout, and a fit-text name so the longer horse names
 * (e.g. "Lightning McWhinny", "Sir Prance-a-Lot") shrink to fit instead of
 * overflowing. The header uses the same big Bebas Neue treatment as the
 * lobby title for visual continuity.
 */

import type { BettingState } from '../state/types.js';
import { fitText } from '../ui/fit-text.js';

export interface BettingHandle {
  el: HTMLDivElement;
  setBetting(b: BettingState): void;
  setVisible(v: boolean): void;
  /** Refresh the countdown ring; called at ~5Hz from main.ts. */
  tick(): void;
}

const STYLES = `
  @keyframes hr-bet-card-in {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes hr-bet-chip-pulse {
    0%   { transform: scale(1.3); filter: brightness(1.5); }
    100% { transform: scale(1);   filter: brightness(1);   }
  }
  @keyframes hr-bet-badge-shine {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.0), 0 6px 0 rgba(0,0,0,0.4); }
    50%      { box-shadow: 0 0 32px 8px rgba(255,255,255,0.15), 0 6px 0 rgba(0,0,0,0.4); }
  }
  .hr-bet {
    position: absolute;
    /* Reserve the right gutter for the side leaderboard (380px wide @ x=1500).
       Cards live on the LEFT (max 1180px wide content area). */
    top: 0; left: 0; right: 460px; bottom: 0;
    display: flex; flex-direction: column;
    align-items: center; justify-content: flex-start;
    padding: 60px 40px 40px 60px;
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    box-sizing: border-box;
    z-index: 2;
  }
  .hr-bet-content {
    width: 100%;
    max-width: 1180px;
    display: flex; flex-direction: column;
    align-items: center;
  }
  .hr-bet-header {
    display: flex; flex-direction: column; align-items: center;
    gap: 8px; margin-bottom: 28px;
  }
  .hr-bet-title {
    font-family: 'Bebas Neue', 'Oswald', sans-serif; font-weight: 700;
    font-size: 110px; letter-spacing: 0.14em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 6px 0 var(--sg-secondary, #3B0764), 0 0 60px rgba(255,196,61,0.3);
    line-height: 0.9;
  }
  .hr-bet-meta {
    display: flex; align-items: center; gap: 28px; margin-top: 4px;
  }
  .hr-bet-race-label {
    font-family: 'Oswald', sans-serif;
    font-size: 30px; font-weight: 700;
    letter-spacing: 0.18em;
    color: #E2E8F0;
  }
  .hr-bet-total {
    font-family: 'Oswald', sans-serif;
    font-size: 22px; letter-spacing: 0.18em;
    color: var(--sg-accent, #22C55E);
    background: rgba(34,197,94,0.12);
    padding: 8px 16px; border-radius: 999px;
    border: 1px solid rgba(34,197,94,0.4);
  }
  .hr-bet-tip {
    font-family: 'Oswald', sans-serif;
    font-size: 22px; letter-spacing: 0.18em;
    color: #94A3B8; margin: 4px 0 22px 0;
    text-transform: uppercase;
  }
  .hr-bet-tip code {
    color: var(--sg-primary, #FFC43D);
    background: rgba(255,196,61,0.16);
    padding: 2px 10px;
    border-radius: 6px;
    font-family: ui-monospace, monospace;
    font-size: 1.1em; letter-spacing: 0;
    margin: 0 2px;
  }
  .hr-bet-board {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 28px; width: 100%;
  }
  .hr-bet-card {
    display: flex; flex-direction: column; align-items: center;
    gap: 18px;
    padding: 36px 22px 30px 22px;
    min-height: 260px;
    background: linear-gradient(180deg, rgba(15,23,42,0.85) 0%, rgba(3,6,23,0.95) 100%);
    border-radius: 22px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.6);
    position: relative;
    overflow: hidden;
    animation: hr-bet-card-in 320ms ease-out backwards;
    border: 3px solid var(--card-silk);
  }
  .hr-bet-card::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 4px;
    background: var(--card-silk);
    box-shadow: 0 0 18px var(--card-silk);
  }
  .hr-bet-card[data-rank="1"] {
    box-shadow: 0 8px 32px rgba(0,0,0,0.55), 0 0 36px var(--card-silk);
  }
  .hr-bet-card:nth-child(1) { animation-delay: 30ms; }
  .hr-bet-card:nth-child(2) { animation-delay: 60ms; }
  .hr-bet-card:nth-child(3) { animation-delay: 90ms; }
  .hr-bet-card:nth-child(4) { animation-delay: 120ms; }
  .hr-bet-card:nth-child(5) { animation-delay: 150ms; }
  .hr-bet-card:nth-child(6) { animation-delay: 180ms; }
  .hr-bet-card:nth-child(7) { animation-delay: 210ms; }
  .hr-bet-card:nth-child(8) { animation-delay: 240ms; }
  .hr-bet-badge {
    position: absolute; top: 18px; left: 18px;
    width: 78px; height: 78px;
    background: var(--card-silk);
    color: #000;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 54px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%;
    box-shadow: inset 0 -10px 0 rgba(0,0,0,0.18), 0 6px 0 rgba(0,0,0,0.4);
    animation: hr-bet-badge-shine 4s ease-in-out infinite;
    text-shadow: 0 2px 0 rgba(255,255,255,0.25);
  }
  .hr-bet-silk-swatch {
    width: 64px; height: 20px; border-radius: 6px;
    background: var(--card-silk);
    box-shadow: 0 0 14px var(--card-silk), inset 0 -3px 0 rgba(0,0,0,0.25);
    margin-top: 84px; /* leave room for the badge above */
  }
  .hr-bet-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    font-size: 28px;
    text-align: center;
    height: 72px;
    line-height: 1.05;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0 6px;
    margin-top: 4px;
  }
  .hr-bet-chip {
    position: absolute; bottom: 18px; right: 18px;
    background: rgba(255,196,61,0.18);
    color: var(--sg-primary, #FFC43D);
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    font-size: 19px;
    letter-spacing: 0.12em;
    padding: 7px 16px;
    border-radius: 999px;
    border: 1px solid rgba(255,196,61,0.5);
    min-width: 88px;
    text-align: center;
    transition: background 200ms ease;
  }
  .hr-bet-card[data-rank="1"] .hr-bet-chip {
    background: rgba(34,197,94,0.22);
    color: var(--sg-celebration, #22C55E);
    border-color: rgba(34,197,94,0.6);
  }
  .hr-bet-chip.hr-bet-chip-pulse {
    animation: hr-bet-chip-pulse 320ms ease-out;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('hr-bet-style')) return;
  const s = document.createElement('style');
  s.id = 'hr-bet-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createBetting(stage: HTMLElement): BettingHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'hr-bet';
  el.setAttribute('data-scene', 'betting');
  el.setAttribute('data-testid', 'hr-betting');

  const header = document.createElement('div');
  header.className = 'hr-bet-header';
  const title = document.createElement('div');
  title.className = 'hr-bet-title';
  title.textContent = 'PLACE YOUR BETS';
  header.appendChild(title);

  const meta = document.createElement('div');
  meta.className = 'hr-bet-meta';
  const raceLabel = document.createElement('div');
  raceLabel.className = 'hr-bet-race-label';
  const totalBetsLabel = document.createElement('div');
  totalBetsLabel.className = 'hr-bet-total';
  totalBetsLabel.textContent = '0 BETS';
  meta.appendChild(raceLabel);
  meta.appendChild(totalBetsLabel);
  header.appendChild(meta);

  const tip = document.createElement('div');
  tip.className = 'hr-bet-tip';
  tip.innerHTML = 'TYPE <code>1</code><code>2</code><code>3</code><code>4</code><code>5</code><code>6</code><code>7</code><code>8</code> IN CHAT';

  const board = document.createElement('div');
  board.className = 'hr-bet-board';
  board.setAttribute('data-testid', 'hr-betting-board');

  const content = document.createElement('div');
  content.className = 'hr-bet-content';
  content.appendChild(header);
  content.appendChild(tip);
  content.appendChild(board);
  el.appendChild(content);
  stage.appendChild(el);

  let current: BettingState | null = null;
  // Cache per-card chip elements + last count so we can pulse on increment.
  const chipState = new Map<string, { chip: HTMLDivElement; lastCount: number }>();

  function rebuildBoard(): void {
    if (!current) {
      board.innerHTML = '';
      chipState.clear();
      return;
    }
    board.innerHTML = '';
    chipState.clear();
    // Find leading horse (highest count) so we can mark its card.
    const counts = current.counts ?? {};
    let leader = -1;
    let leaderCount = 0;
    for (const h of current.horses) {
      const c = counts[String(h.number)] ?? 0;
      if (c > leaderCount) { leaderCount = c; leader = h.number; }
    }
    for (const h of current.horses) {
      const card = document.createElement('div');
      card.className = 'hr-bet-card';
      card.setAttribute('data-horse-number', String(h.number));
      card.style.setProperty('--card-silk', h.silkColor);
      if (h.number === leader && leaderCount > 0) card.setAttribute('data-rank', '1');

      const badge = document.createElement('div');
      badge.className = 'hr-bet-badge';
      badge.textContent = String(h.number);
      card.appendChild(badge);

      const swatch = document.createElement('div');
      swatch.className = 'hr-bet-silk-swatch';
      card.appendChild(swatch);

      const name = document.createElement('div');
      name.className = 'hr-bet-name';
      name.textContent = h.name;
      card.appendChild(name);

      const chip = document.createElement('div');
      chip.className = 'hr-bet-chip';
      const c = counts[String(h.number)] ?? 0;
      chip.textContent = `${c} BET${c === 1 ? '' : 'S'}`;
      card.appendChild(chip);

      board.appendChild(card);
      chipState.set(String(h.number), { chip, lastCount: c });
      // Defer fit so layout has settled.
      requestAnimationFrame(() => fitText(name, { min: 12, max: 24, lineHeight: 1.05 }));
    }
  }

  function refreshChipsAndLeader(): void {
    if (!current) return;
    const counts = current.counts ?? {};
    let leader = -1;
    let leaderCount = 0;
    for (const num in counts) {
      const c = counts[num] ?? 0;
      if (c > leaderCount) { leaderCount = c; leader = Number(num); }
    }
    for (const [num, st] of chipState) {
      const c = counts[num] ?? 0;
      if (c !== st.lastCount) {
        st.chip.textContent = `${c} BET${c === 1 ? '' : 'S'}`;
        if (c > st.lastCount) {
          st.chip.classList.remove('hr-bet-chip-pulse');
          // Reflow forces the animation to restart.
          void st.chip.offsetWidth;
          st.chip.classList.add('hr-bet-chip-pulse');
        }
        st.lastCount = c;
      }
    }
    // Update leader card highlight.
    const cards = board.querySelectorAll<HTMLDivElement>('[data-horse-number]');
    cards.forEach((card) => {
      const num = Number(card.getAttribute('data-horse-number') ?? '0');
      if (num === leader && leaderCount > 0) card.setAttribute('data-rank', '1');
      else card.removeAttribute('data-rank');
    });
    totalBetsLabel.textContent = `${current.totalBets} BET${current.totalBets === 1 ? '' : 'S'}`;
  }

  return {
    el,
    setBetting(b) {
      const isNewRace = !current || current.raceId !== b.raceId;
      current = b;
      // racesTotal = 0 sentinel = continuous play; show "RACE #N" instead.
      raceLabel.textContent = b.racesTotal > 0
        ? `RACE ${b.raceIndex + 1} OF ${b.racesTotal}`
        : `RACE #${b.raceIndex + 1}`;
      if (isNewRace) {
        rebuildBoard();
      } else {
        refreshChipsAndLeader();
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
    tick() {
      if (current) refreshChipsAndLeader();
    },
  };
}
